import { useState, useEffect } from "react";
import useWebSocket from "react-use-websocket";
import { getWebSocketUrl, getTextChatUrl } from '../config/api';

import {
    InputAudioBufferAppendCommand,
    InputAudioBufferClearCommand,
    Message,
    ResponseAudioDelta,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    SessionUpdateCommand,
    ExtensionMiddleTierToolResponse,
    ResponseInputAudioTranscriptionCompleted,
    EventData,
    type ThinkingBannerKind,
} from "@/types";

var text = ''

/** GA Realtime `session.audio.*.format` is an object (e.g. `{ type: 'audio/pcm', rate: 24000 }`), not `pcm16` string. */
function coerceGaAudioFormatValue(value: unknown): { type: string; rate?: number } {
    if (value != null && typeof value === "object" && "type" in (value as object)) {
        return value as { type: string; rate?: number };
    }
    if (value === "pcm16") return { type: "audio/pcm", rate: 24000 };
    if (value === "g711_ulaw" || value === "g711-ulaw") return { type: "audio/pcmu" };
    if (value === "g711_alaw" || value === "g711-alaw") return { type: "audio/pcma" };
    return { type: "audio/pcm", rate: 24000 };
}

/** GA `session.type: "realtime"` must not use preview-era flat audio keys (e.g. top-level `turn_detection`). */
function normalizeRealtimeSessionUpdateForGa(message: unknown): any {
    if (!message || typeof message !== "object") return message;
    const m = message as { type?: string; session?: Record<string, unknown> };
    if (m.type !== "session.update" || !m.session || typeof m.session !== "object") return message;

    const session = m.session;
    if (session.type !== "realtime") return message;

    // GA requires `format` as an object under audio.* — fix nested string `pcm16` without legacy top-level keys.
    if (session.audio && typeof session.audio === "object") {
        const audio = session.audio as Record<string, unknown>;
        if (audio.input && typeof audio.input === "object") {
            const inp = audio.input as Record<string, unknown>;
            if (typeof inp.format === "string") {
                inp.format = coerceGaAudioFormatValue(inp.format);
            }
        }
        if (audio.output && typeof audio.output === "object") {
            const out = audio.output as Record<string, unknown>;
            if (typeof out.format === "string") {
                out.format = coerceGaAudioFormatValue(out.format);
            }
        }
    }

    const legacyKeys = [
        "turn_detection",
        "input_audio_format",
        "input_audio_transcription",
        "output_audio_format",
        "modalities",
    ] as const;
    if (!legacyKeys.some((k) => k in session)) {
        return { ...m, session: { ...session } };
    }

    const audio = { ...(typeof session.audio === "object" && session.audio ? (session.audio as object) : {}) } as Record<
        string,
        unknown
    >;
    let input = { ...(typeof audio.input === "object" && audio.input ? (audio.input as object) : {}) } as Record<
        string,
        unknown
    >;
    let output = { ...(typeof audio.output === "object" && audio.output ? (audio.output as object) : {}) } as Record<
        string,
        unknown
    >;

    if ("turn_detection" in session) {
        input = { ...input, turn_detection: session.turn_detection };
        delete session.turn_detection;
    }
    if ("input_audio_format" in session) {
        input = { ...input, format: session.input_audio_format };
        delete session.input_audio_format;
    }
    if ("input_audio_transcription" in session) {
        input = { ...input, transcription: session.input_audio_transcription };
        delete session.input_audio_transcription;
    }
    if ("output_audio_format" in session) {
        output = { ...output, format: session.output_audio_format };
        delete session.output_audio_format;
    }

    if ("modalities" in session && !("output_modalities" in session)) {
        (session as { output_modalities?: unknown }).output_modalities = session.modalities;
        delete session.modalities;
    }

    if (typeof input.format === "string") {
        input.format = coerceGaAudioFormatValue(input.format);
    }
    if (typeof output.format === "string") {
        output.format = coerceGaAudioFormatValue(output.format);
    }

    if (Object.keys(input).length) audio.input = input;
    if (Object.keys(output).length) audio.output = output;
    if (Object.keys(audio).length) {
        session.audio = audio;
    }
    return { ...m, session: { ...session } };
}

type Parameters = {
    useDirectAoaiApi?: boolean; // If true, the middle tier will be skipped and the AOAI ws API will be called directly
    aoaiEndpointOverride?: string;
    aoaiApiKeyOverride?: string;
    aoaiModelOverride?: string;

    enableInputAudioTranscription?: boolean;
    onWebSocketOpen?: () => void;
    onWebSocketClose?: () => void;
    onWebSocketError?: (event: Event) => void;
    onWebSocketMessage?: (event: MessageEvent<any>) => void;

    onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedError?: (message: Message) => void;
    setIsThinking: (isThinking: boolean) => void;
    /** When set, middle tier can switch voice UI between "Thinking..." and "Searching...". */
    setThinkingBanner?: (kind: ThinkingBannerKind) => void;
    setTextChatHistory: (id: string, text: string, type: string, replace?: boolean) => void;
    /** When set, appended to `/realtime` as `user_email` for Azure SQL conversation logging. */
    userEmail?: string | null;
};

export default function useRealTime({
    useDirectAoaiApi,
    aoaiEndpointOverride,
    aoaiApiKeyOverride,
    aoaiModelOverride,
    enableInputAudioTranscription,
    onWebSocketOpen,
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onReceivedResponseDone,
    onReceivedResponseAudioDelta,
    onReceivedResponseAudioTranscriptDelta,
    onReceivedInputAudioBufferSpeechStarted,
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedError,
    setIsThinking,
    setThinkingBanner,
    setTextChatHistory,
    userEmail,
}: Parameters) {
    const wsEndpoint = useDirectAoaiApi
        ? `${aoaiEndpointOverride}/openai/realtime?api-key=${aoaiApiKeyOverride}&deployment=${aoaiModelOverride}&api-version=2024-10-01-preview`
        : getWebSocketUrl(undefined, userEmail ?? undefined);

    // Queue for pending messages when WebSocket is not ready
    const [pendingMessages, setPendingMessages] = useState<any[]>([]);
    const [isWebSocketReady, setIsWebSocketReady] = useState(false);
    // React 18 Strict Mode mounts → effect → unmount → remount. Opening the socket on the first
    // mount leaves it in CONNECTING when cleanup closes it, which spams "closed before established".
    // Defer connect until after that cycle (timer cleared on the fake unmount).
    const [shouldConnectWs, setShouldConnectWs] = useState(false);
    useEffect(() => {
        const id = window.setTimeout(() => setShouldConnectWs(true), 120);
        return () => window.clearTimeout(id);
    }, []);

    const { sendJsonMessage } = useWebSocket(
        wsEndpoint,
        {
            onOpen: () => {
                console.log("WebSocket connection opened");
                setIsWebSocketReady(true);
                onWebSocketOpen?.();
            },
            onClose: () => {
                console.log("WebSocket connection closed");
                setIsWebSocketReady(false);
                onWebSocketClose?.();
            },
            onError: event => {
                console.error("WebSocket error:", event);
                setIsWebSocketReady(false);
                onWebSocketError?.(event);
            },
            onMessage: event => onMessageReceived(event),
            shouldReconnect: () => true,
        },
        shouldConnectWs,
    );

    // Process pending messages when WebSocket becomes ready
    useEffect(() => {
        if (!isWebSocketReady || pendingMessages.length === 0) {
            return;
        }
        // Strict Mode double-mount can queue duplicate session.update; sending both often triggers Azure errors.
        const lastSessionUpdate = [...pendingMessages]
            .reverse()
            .find((m) => m?.type === "session.update");
        const nonSession = pendingMessages.filter((m) => m?.type !== "session.update");
        const toSend = lastSessionUpdate ? [...nonSession, lastSessionUpdate] : nonSession;

        console.log(
            `Processing ${pendingMessages.length} pending message(s) as ${toSend.length} after deduping session.update`,
        );
        toSend.forEach((message) => {
            try {
                const out = normalizeRealtimeSessionUpdateForGa(message);
                sendJsonMessage(out);
                console.log("Sent pending message:", out);
            } catch (error) {
                console.error("Failed to send pending message:", error);
            }
        });
        setPendingMessages([]);
    }, [isWebSocketReady, pendingMessages, sendJsonMessage]);

    // Safe message sending function that queues messages if WebSocket is not ready
    const safeSendJsonMessage = (message: any) => {
        const out = normalizeRealtimeSessionUpdateForGa(message);
        if (isWebSocketReady && sendJsonMessage) {
            try {
                sendJsonMessage(out);
                console.log('Message sent immediately:', out);
            } catch (error) {
                console.error('Failed to send message:', error);
            }
        } else {
            console.log("WebSocket not ready, queuing message:", out);
            setPendingMessages((prev) => {
                // Replace any queued session.update with the latest (avoids duplicate session.update on React Strict Mode)
                if (out?.type === "session.update") {
                    return [...prev.filter((m) => m?.type !== "session.update"), out];
                }
                return [...prev, out];
            });
        }
    };

    const startSession = async (topic?: string) => {
        console.log('=== WebSocket Session Debug ===');
        console.log('Topic passed to startSession:', topic);
        console.log('Current URL when starting session:', window.location.href);
        console.log('================================');
        
        // If topic is present, pre-search to get context
        if (topic) {
            try {
                console.log(`Pre-searching for topic: ${topic}`);
                const searchResponse = await fetch(getTextChatUrl(), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        text: `Search for information about ${topic}`,
                        session_id: `pre_search_${Date.now()}`,
                        topic: topic
                    })
                });
                
                if (searchResponse.ok) {
                    const searchData = await searchResponse.json();
                    const searchContext = searchData.message || searchData.text || 'No search results';
                    console.log('Pre-search completed, context:', searchContext.substring(0, 100) + '...');
                    
                    // Store search context for the AI to use
                    localStorage.setItem(`topic_context_${topic}`, searchContext);
                }
            } catch (error) {
                console.error('Pre-search failed:', error);
            }
        }
        
        const command: SessionUpdateCommand = {
            type: "session.update",
            session: {
                type: "realtime",
                audio: {
                    input: {
                        format: { type: "audio/pcm", rate: 24000 },
                        turn_detection: { type: "server_vad" },
                        ...(enableInputAudioTranscription
                            ? { transcription: { model: "whisper-1" as const } }
                            : {}),
                    },
                    output: { format: { type: "audio/pcm", rate: 24000 } },
                },
                ...(topic ? { topic } : {}),
            },
        };

        if (topic) {
            console.log('DEBUG: Sending topic to backend:', topic);
        } else {
            console.log('DEBUG: No topic provided to backend');
        }

        console.log('DEBUG: Session command:', JSON.stringify(command, null, 2));
        safeSendJsonMessage(command);
    };

    const addUserAudio = (base64Audio: string) => {
        const command: InputAudioBufferAppendCommand = {
            type: "input_audio_buffer.append",
            audio: base64Audio
        };

        safeSendJsonMessage(command);
    };

    const inputAudioBufferClear = () => {
        const command: InputAudioBufferClearCommand = {
            type: "input_audio_buffer.clear"
        };

        safeSendJsonMessage(command);
    };
    const onMessageReceived = (event: MessageEvent<any>) => {
        onWebSocketMessage?.(event);

        let message: Message;
        let eventData: EventData;
        try {
            message = JSON.parse(event.data);
            eventData = JSON.parse(event.data);
        } catch (e) {
            console.error("Failed to parse JSON message:", e);
            throw e;
        }
        switch (message.type) {
            case "response.done":
                onReceivedResponseDone?.(message as ResponseDone);
                break;
            case "response.audio.delta":
            case "response.output_audio.delta":
                // GA Realtime uses response.output_audio.delta; preview uses response.audio.delta
                onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
                break;
            case "response.audio_transcript.delta":
            case "response.output_audio_transcript.delta": {
                const audioTranscriptMessage = message as ResponseAudioTranscriptDelta;
                text = text + audioTranscriptMessage.delta
                // Use eventData for item_id since ResponseAudioTranscriptDelta doesn't have it
                setTextChatHistory(eventData?.item_id || 'default', audioTranscriptMessage.delta, 'output')
                onReceivedResponseAudioTranscriptDelta?.(audioTranscriptMessage);
                break;
            }
            case "input_audio_buffer.speech_started":
                onReceivedInputAudioBufferSpeechStarted?.(message);
                break;
            case "conversation.item.input_audio_transcription.completed":
                const inputTranscriptionMessage = message as ResponseInputAudioTranscriptionCompleted;
                setTextChatHistory(inputTranscriptionMessage.item_id, inputTranscriptionMessage.transcript, 'input')

                // Show thinking as soon as the user turn is finalized. A fixed 2s delay meant fast tool
                // searches finished before the indicator appeared; empty audio deltas could also cancel
                // the pending timer via the audio branch without ever showing thinking.
                setIsThinking(true);

                onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
                break;
            case "extension.middle_tier_tool_started": {
                const m = message as Message & {
                    tool_name?: string;
                    call_id?: string;
                    previous_item_id?: string;
                    query?: string;
                };
                setThinkingBanner?.(m.tool_name === "search" ? "searching" : "thinking");
                setIsThinking(true);
                // Live transcript can be wrong in noisy environments; when search starts, use the
                // actual query that the model/tool is executing for the visible user line.
                if (m.tool_name === "search" && typeof m.query === "string" && m.query.trim()) {
                    const inputId = (m.previous_item_id || m.call_id || "default").toString();
                    setTextChatHistory(inputId, m.query.trim(), "input", true);
                }
                break;
            }
            case "extension.middle_tier_tool_response":
                onReceivedExtensionMiddleTierToolResponse?.(message as ExtensionMiddleTierToolResponse);
                break;
            case "error":
                console.error("Realtime error event:", JSON.stringify(message, null, 2));
                onReceivedError?.(message);
                break;
        }
    };

    return { startSession, addUserAudio, inputAudioBufferClear, sendJsonMessage: safeSendJsonMessage };
}
